import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI, subscriptionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Sparkles, Plus, Settings, CreditCard, LogOut, 
  FolderOpen, ChevronRight, Sun, Moon, Globe,
  LayoutDashboard, Zap, Clock, Trash2, MoreVertical,
  Flame, Shield, Crown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { t, language, setLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, usage, logout, refreshUsage, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
    checkPaymentStatus();
    refreshUsage();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      try {
        const response = await subscriptionsAPI.getStatus(sessionId);
        if (response.data.payment_status === 'paid') {
          toast.success('Subscription activated successfully!');
          refreshUsage();
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
      }
      // Clear the URL
      window.history.replaceState({}, '', '/dashboard');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await projectsAPI.delete(projectId);
      setProjects(projects.filter(p => p.project_id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const getPlanDisplayName = (plan) => {
    switch (plan) {
      case 'admin': return 'Admin';
      case 'free_trial': return 'Free Trial';
      case 'starter': return 'Starter';
      case 'pro': return 'Pro';
      case 'agency': return 'Agency';
      default: return 'Free Trial';
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'admin': return 'bg-gradient-to-r from-amber-700 to-orange-700 text-white';
      case 'pro': return 'bg-primary text-primary-foreground';
      case 'agency': return 'bg-gradient-to-r from-primary to-accent text-white';
      case 'starter': return 'bg-blue-500 text-white';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const renderUsageWidget = () => {
    if (!usage) return null;

    // Admin Mode
    if (isAdmin || usage.is_admin) {
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30"
          data-testid="admin-usage-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-500" />
              <h3 className="text-sm font-medium">Admin Mode Active</h3>
            </div>
            <Badge className="bg-gradient-to-r from-amber-700 to-orange-700 text-white">
              <Crown className="w-3 h-3 mr-1" />
              Admin
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Unlimited generations • No restrictions • Full access
          </p>
        </motion.div>
      );
    }

    // Free Trial Mode
    if (usage.subscription_plan === 'free_trial') {
      const freeLimit = usage.generations_limit || 20;
      const usedGenerations = freeLimit - usage.free_trial_remaining;
      const percentUsed = freeLimit > 0 ? (usedGenerations / freeLimit) * 100 : 0;
      
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
          data-testid="free-trial-usage-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <h3 className="text-sm font-medium text-muted-foreground">Free Generations</h3>
            </div>
            <Badge variant="secondary">Free Trial</Badge>
          </div>
          <div className="text-3xl font-heading font-bold mb-2 flex items-center gap-2">
            <span className={usage.free_trial_remaining === 0 ? 'text-destructive' : ''}>
              {usage.free_trial_remaining}
            </span>
            <span className="text-muted-foreground text-lg font-normal">/ {freeLimit} remaining</span>
          </div>
          <Progress value={100 - percentUsed} className="h-2 mb-3" />
          {usage.free_trial_remaining === 0 ? (
            <Link to="/billing">
              <Button size="sm" className="w-full">Upgrade to Continue</Button>
            </Link>
          ) : (
            <p className="text-xs text-muted-foreground">
              Use all features during your free trial
            </p>
          )}
        </motion.div>
      );
    }

    // Paid Plan Mode
    const percentUsed = (usage.generations_used / usage.generations_limit) * 100;
    const isUnlimited = usage.generations_limit >= 999999;
    
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card"
        data-testid="paid-usage-card"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-muted-foreground">{t('dashboard.adsUsed')}</h3>
          <Zap className="w-5 h-5 text-primary" />
        </div>
        <div className="text-3xl font-heading font-bold mb-2">
          {usage.generations_used} / {isUnlimited ? '∞' : usage.generations_limit}
        </div>
        {!isUnlimited && <Progress value={percentUsed} className="h-2" />}
        <p className="text-xs text-muted-foreground mt-2">
          Resets monthly • {usage.generations_remaining} remaining
        </p>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-40 hidden lg:block">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg">AIgnyx</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <Link 
              to="/dashboard"
              className="sidebar-item active flex items-center gap-3 px-4 py-3 rounded-xl"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              {t('dashboard.title')}
            </Link>
            <Link 
              to="/generator"
              className="sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl"
              data-testid="nav-generator"
            >
              <Zap className="w-5 h-5" />
              {t('generator.title')}
            </Link>
            <Link 
              to="/projects"
              className="sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl"
              data-testid="nav-projects"
            >
              <FolderOpen className="w-5 h-5" />
              {t('dashboard.projects')}
            </Link>
          </nav>

          {/* Bottom Actions */}
          <div className="p-4 border-t border-border space-y-2">
            <Link 
              to="/settings"
              className="sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl"
            >
              <Settings className="w-5 h-5" />
              {t('dashboard.settings')}
            </Link>
            <Link 
              to="/billing"
              className="sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl"
            >
              <CreditCard className="w-5 h-5" />
              {t('dashboard.billing')}
            </Link>
            <button 
              onClick={handleLogout}
              className="sidebar-item flex items-center gap-3 px-4 py-3 rounded-xl w-full text-left text-destructive"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="lg:hidden">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              </Link>
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold">{t('dashboard.title')}</h1>
              {isAdmin && (
                <Badge className="bg-gradient-to-r from-amber-700 to-orange-700 text-white">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-4">
              {/* Language */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}>
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Theme */}
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <span className="text-sm font-medium text-primary">
                          {user?.name?.charAt(0) || 'U'}
                        </span>
                      )}
                    </div>
                    <span className="hidden sm:inline font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    {t('dashboard.settings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/billing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t('dashboard.billing')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-6 max-w-7xl mx-auto">
          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Usage Card - Dynamic based on plan */}
            {renderUsageWidget()}

            {/* Plan Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="dashboard-card"
              data-testid="plan-card"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-muted-foreground">{t('dashboard.plan')}</h3>
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <div className="flex items-center gap-3">
                <Badge className={`${getPlanBadgeColor(usage?.subscription_plan || user?.subscription_plan)}`}>
                  {getPlanDisplayName(usage?.subscription_plan || user?.subscription_plan)}
                </Badge>
                {!isAdmin && usage?.subscription_plan === 'free_trial' && (
                  <Link to="/billing">
                    <Button variant="link" size="sm" className="p-0 h-auto">
                      Upgrade
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>

            {/* Quick Action Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="dashboard-card bg-primary text-primary-foreground"
            >
              <h3 className="text-sm font-medium opacity-80 mb-4">{t('dashboard.newProject')}</h3>
              <Link to="/generator">
                <Button className="w-full bg-white text-primary hover:bg-white/90 shadow-md" data-testid="new-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('generator.title')}
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* Recent Projects */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-semibold">{t('dashboard.recentProjects')}</h2>
              <Link to="/projects">
                <Button variant="ghost" size="sm">
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="dashboard-card animate-pulse">
                    <div className="h-4 bg-secondary rounded w-3/4 mb-3" />
                    <div className="h-3 bg-secondary rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : projects.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12 bg-card rounded-2xl border border-border"
                data-testid="no-projects"
              >
                <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">{t('dashboard.noProjects')}</p>
                <Link to="/generator">
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Ad
                  </Button>
                </Link>
              </motion.div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.slice(0, 6).map((project, index) => (
                  <motion.div
                    key={project.project_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="dashboard-card card-hover group"
                    data-testid={`project-${project.project_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold truncate flex-1 mr-2">{project.name}</h3>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/generator?project=${project.project_id}`)}>
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteProject(project.project_id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <p className="text-sm text-muted-foreground truncate mb-3">
                      {project.product_input?.product_name || 'Untitled Product'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {new Date(project.updated_at).toLocaleDateString()}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
